import { useRef } from 'react';
import { Table } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import type { ServiceType } from '@econtractor/types';
import { useAuth } from 'context/AuthContext';
import { hasUserAccess } from 'utils/helpers/access/hasUserAccess';
import SpinnerBlock from 'components/others/SpinnerBlock';
import TableHeadTdOrder from 'components/others/TableHeadTdOrder';
import OffServiceCard from './OffServiceCard';
import ServiceTableBody from './ServiceTableBody';
import { useService } from './context/ServiceProvider';

interface ServicesTableProps {
    servicesList: ServiceType[];
    fetchServices: (force?: boolean) => Promise<unknown>;
}

const ServicesTable = ({ servicesList, fetchServices }: ServicesTableProps) => {
    const { auth } = useAuth();
    const { t } = useTranslation(['Services']);
    const { showSpinner, servicesOrder, servicesDesc, setServicesOrder, setServicesDesc, serviceCheck, setServiceCheck } =
        useService();

    const refCheckAll = useRef<HTMLInputElement>(null);
    const allServicesIdOnPage = servicesList?.map((s) => s.ID) || [];

    const handleOrderClick = (value: string, desc: boolean) => {
        setServicesOrder(value);
        setServicesDesc(desc);
    };

    return (
        <>
            <OffServiceCard fetchServices={fetchServices} />

            <SpinnerBlock onlyBlur={true} show={showSpinner} />
            <div className="crm-table-wrap">
                <Table className="ServiceTable table-hover">
                    <thead>
                        <tr className="bg-light">
                            <td className="head-check">
                                <input
                                    className="form-check-input me-2"
                                    ref={refCheckAll}
                                    type="checkbox"
                                    disabled={!hasUserAccess(auth?.user, 'prodEdit')}
                                    checked={allServicesIdOnPage.every((id) => serviceCheck.includes(id))}
                                    onChange={(e) => {
                                        setServiceCheck((prev) => {
                                            if (e.target.checked) {
                                                const union = Array.from(new Set([...prev, ...allServicesIdOnPage]));
                                                return [...union];
                                            }
                                            return prev.filter((s) => !allServicesIdOnPage.includes(s));
                                        });
                                    }}
                                />
                            </td>
                            <td className="head-name">
                                <TableHeadTdOrder
                                    title={t('fields.name')}
                                    onClick={handleOrderClick}
                                    value={servicesOrder}
                                    desc={servicesDesc}
                                    fieldName="serv_name"
                                />
                            </td>

                            <td className="head-vendor">
                                <TableHeadTdOrder
                                    title={t('fields.category')}
                                    onClick={handleOrderClick}
                                    value={servicesOrder}
                                    desc={servicesDesc}
                                    fieldName="category_name"
                                />
                            </td>

                            <td className="head-id">
                                <TableHeadTdOrder
                                    title={t('fields.id')}
                                    onClick={handleOrderClick}
                                    value={servicesOrder}
                                    desc={servicesDesc}
                                    fieldName="ID"
                                />
                            </td>

                            <td className="head-article">
                                <TableHeadTdOrder
                                    title={t('fields.article')}
                                    onClick={handleOrderClick}
                                    value={servicesOrder}
                                    desc={servicesDesc}
                                    fieldName="serv_article"
                                />
                            </td>

                            <td className="head-price">{t('fields.priceList')}</td>

                            <td className="head-updated">
                                <TableHeadTdOrder
                                    title={t('fields.updated')}
                                    onClick={handleOrderClick}
                                    value={servicesOrder}
                                    desc={servicesDesc}
                                    fieldName="serv_updated"
                                />
                            </td>

                            {hasUserAccess(auth?.user, 'prodEdit') && <td className="head-action">{t('fields.actions')}</td>}
                        </tr>
                    </thead>

                    <ServiceTableBody servicesList={servicesList} fetchServices={fetchServices} />
                </Table>
            </div>
        </>
    );
};

export default ServicesTable;
